import { NewPaymentMethodDelegate } from 'billing/payment-methods/PaymentMethodsRootViewModel'
import Member from 'iam/models/Member'
import ProfileGeneralViewModel from './General/ProfileGeneralViewModel'
import ProfileBillingViewModel from './Billing/ProfileBillingViewModel'
import { task } from 'mobx-task'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { UpdateProfileParams } from 'iam/MemberStore'
import Skill from 'iam/models/Skill'
import Role from 'iam/models/Role'
import Workspace from 'iam/models/Workspace'
import ProfileNotificationsViewModel from './Notifications/ProfileNotificationsViewModel'
import { NotificationPreferences } from 'notifications/dtos/notifications'
import BillingStore from 'billing/BillingStore'
import { computed, makeObservable } from 'mobx'
import { CursorPaged, CursorPageQuery, Router } from 'types/definitions'
import PayoutMethodStore from 'payouts/pro/PayoutMethodStore'
import ProviderStore from 'iam/ProviderStore'
import UserDataStore from 'iam/UserDataStore'
import { UserDataType } from 'iam/models/UserDataType'
import ProDetailsViewModel from 'iam/pro-details/ProDetailsViewModel'

export default class ProfileViewModel {
  /**
   * General tab view model.
   */
  general = new ProfileGeneralViewModel(
    this.newAlert,
    this.member,
    this.iamPort,
  )

  /**
   * Pro details (provider profile) view model.
   */
  proDetails = new ProDetailsViewModel(
    this.newAlert,
    this.iamProviderStore,
    this.member.user.publicId,
    true,
  )

  /**
   * Billing tab view model.
   */
  billing = new ProfileBillingViewModel(
    this.member,
    this.billingStore,
    this.newPaymentMethod,
    this.payoutMethodStore,
    this.router,
  )

  /**
   * Notifications tab view model.
   */
  notifications = new ProfileNotificationsViewModel(
    this.member,
    this.newAlert,
    this.notificationsPort,
  )

  constructor(
    public member: Member,
    private newAlert: AlertFactory,
    private iamPort: IamPort,
    private notificationsPort: NotificationsPort,
    private billingStore: BillingStore,
    private newPaymentMethod: NewPaymentMethodDelegate,
    private payoutMethodStore: PayoutMethodStore,
    private router: Router,
    private iamProviderStore: ProviderStore,
    private userDataStore: UserDataStore,
  ) {
    makeObservable(this)
  }

  @computed
  get isPro() {
    return Boolean(
      this.userDataStore.getByPublicId(this.member.user.publicId)
        ?.userDataType === UserDataType.Pro
    )
  }

  @computed
  get canViewAndEditOwnSkills() {
    return this.member.hasPermission('UPDATE_OWN_SKILLS')
  }

  @computed
  get canViewAndEditOwnRoles() {
    return this.member.hasPermission('ADMIN_USER_ROLES_MODIFY')
  }

  @computed
  get shouldShowBilling() {
    return this.billingStore.billingEnabledForCurrentWorkspace
  }

  /**
   * Uploads a new avatar.
   */
  uploadNewAvatar = task.resolved(async (file: File) => {
    try {
      // 10mb, give or take.
      if (file.size > 1024 * 1024 * 10) {
        this.newAlert()
          .withState(AlertState.Error)
          .saying('File is too big', 'Please keep it at 10mb or less.')
          .autoDismiss()
          .show()
        return
      }
      await this.iamPort.changeAvatar(this.member, file)
      this.newAlert()
        .withState(AlertState.Success)
        .saying('Success!', 'Your avatar has been changed.')
        .autoDismiss()
        .show()
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  })
}

/**
 * IAM port required by the view model.
 */
export interface IamPort {
  /**
   * Delegate for fetching the member details, ensuring we populate all the required fields.
   *
   * @param member the member to fetch details for
   */
  fetchMemberDetails(member: Member): Promise<Member | null>

  /**
   * Delegate for changing avatar.
   *
   * @param member the member to change the avatar for
   * @param file the avatar file
   */
  changeAvatar(member: Member, file: File): Promise<unknown>

  /**
   * Updates a Member's profile.
   * @param member
   * @param attrs
   */
  updateProfile(member: Member, attrs: UpdateProfileParams): Promise<unknown>

  /**
   * Fetches skills.
   * @param workspace
   */
  fetchSkills(
    workspace: Workspace,
    query: CursorPageQuery,
  ): Promise<CursorPaged<Skill>>

  /**
   * Delegate for adding a skill.
   * @param member
   * @param skill
   */
  addSkill(member: Member, skill: Skill): Promise<unknown>

  /**
   * Delegate for removing a skill.
   * @param member
   * @param skill
   */
  removeSkill(member: Member, skill: Skill): Promise<unknown>

  /**
   * Fetches roles.
   * @param workspace
   */
  fetchRoles(workspace: Workspace): Promise<Role[]>

  /**
   * Delegate for adding a role.
   * @param workspace_id
   * @param user_public_id
   * @param role_id
   */
  addRole(
    workspace_id: string,
    user_public_id: string,
    role_id: string,
  ): Promise<unknown>

  /**
   * Delegate for removing a role.
   * @param member
   * @param role
   */
  removeRole(member: Member, role: Role): Promise<unknown>
}

/**
 * Port for Notifications.
 */
export interface NotificationsPort {
  /**
   * Delegate for getting preferences.
   * @param member
   */
  getPreferences(member: Member): Promise<NotificationPreferences>

  /**
   * Delegate for getting preferences.
   * @param member
   * @param preferences
   */
  savePreferences(
    member: Member,
    preferences: NotificationPreferences,
  ): Promise<unknown>
}
